/**
 * Schema.org structured data generators for SEO rich snippets
 * Provides markup for artworks, auctions, and other content types
 */

interface ArtworkData {
  id: string;
  title: string;
  artist?: {
    id: string;
    name: string;
    birth_year?: number | null;
    death_year?: number | null;
  } | null;
  year?: number | null;
  medium?: string | null;
  dimensions?: string | null;
  description?: string | null;
  image_url?: string | null;
  estimate_low?: number | null;
  estimate_high?: number | null;
  condition?: string | null;
  provenance?: string | null;
}

interface AuctionData {
  id: string;
  title: string;
  description?: string | null;
  start_date: string;
  end_date?: string | null;
  status: string;
  is_live?: boolean;
  lots?: any[];
}

const BASE_URL = 'https://floridahighwaymenauction.com';

/**
 * Generate Schema.org CreativeWork markup for individual artworks
 */
export const generateArtworkSchema = (artwork: ArtworkData): object => {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "VisualArtwork",
    "@id": `${BASE_URL}/artworks/${artwork.id}`,
    "name": artwork.title,
    "url": `${BASE_URL}/artworks/${artwork.id}`,
    "description": artwork.description || `${artwork.title} by ${artwork.artist?.name || 'Unknown Artist'}`,
    "genre": "Florida Highwaymen Art",
    "artform": "Painting",
    "artMedium": artwork.medium || "Oil on canvas",
    "artworkSurface": "Canvas",
    // Provide dimensions as a property value if available
    "dateCreated": artwork.year?.toString(),
    "material": artwork.medium,
    "artEdition": "Original",
    "copyrightHolder": {
      "@type": "Organization",
      "name": "Florida Highwaymen Auction LLC"
    },
    "provider": {
      "@type": "Organization",
      "name": "Florida Highwaymen Auction LLC",
      "url": BASE_URL
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${BASE_URL}/artworks/${artwork.id}`
    }
  };

  // Add artist information
  if (artwork.artist) {
    schema.creator = {
      "@type": "Person",
      "name": artwork.artist.name,
      "jobTitle": "Artist",
      "description": "Florida Highwaymen Artist",
      "memberOf": {
        "@type": "Organization",
        "name": "Florida Highwaymen"
      }
    };

    if (artwork.artist.birth_year) {
      schema.creator.birthDate = artwork.artist.birth_year.toString();
    }
    if (artwork.artist.death_year) {
      schema.creator.deathDate = artwork.artist.death_year.toString();
    }
  }

  // Add image
  if (artwork.image_url) {
    schema.image = {
      "@type": "ImageObject",
      "url": artwork.image_url.startsWith('http') ? artwork.image_url : `${BASE_URL}${artwork.image_url}`,
      "description": `Image of ${artwork.title}`
    };
  }

  // Add offers/pricing information
  if (artwork.estimate_low || artwork.estimate_high) {
    schema.offers = {
      "@type": "Offer",
      "category": "Auction",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
      "seller": {
        "@type": "Organization",
        "name": "Florida Highwaymen Auction LLC"
      }
    };

    if (artwork.estimate_low && artwork.estimate_high) {
      schema.offers.priceSpecification = {
        "@type": "PriceSpecification",
        "minPrice": artwork.estimate_low,
        "maxPrice": artwork.estimate_high,
        "priceCurrency": "USD"
      };
    } else if (artwork.estimate_low) {
      schema.offers.price = artwork.estimate_low;
      schema.offers.priceCurrency = "USD";
    }
  }

  // Add condition and provenance
  if (artwork.condition) {
    schema.conditionNote = artwork.condition;
  }

  if (artwork.provenance) {
    schema.additionalProperty = {
      "@type": "PropertyValue",
      "name": "Provenance",
      "value": artwork.provenance
    };
  }

  // Add dimensions as a PropertyValue if we have a dimensions string
  if (artwork.dimensions) {
    if (!schema.additionalProperty) {
      schema.additionalProperty = [] as any;
    }
    const existing = Array.isArray(schema.additionalProperty) ? schema.additionalProperty : [schema.additionalProperty];
    existing.push({
      "@type": "PropertyValue",
      "name": "Dimensions",
      "value": artwork.dimensions
    });
    schema.additionalProperty = existing;
  }

  return schema;
};

/**
 * Generate Schema.org Event markup for auctions
 */
export const generateAuctionSchema = (auction: AuctionData): object => {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Event",
    "@id": `${BASE_URL}/auctions/${auction.id}`,
    "name": auction.title,
    "url": `${BASE_URL}/auctions/${auction.id}`,
    "description": auction.description || `Florida Highwaymen art auction featuring ${auction.lots?.length || 'multiple'} lots of authentic paintings.`,
    "eventStatus": auction.status === 'live' || auction.is_live ? 
      "https://schema.org/EventScheduled" : 
      auction.status === 'completed' ? 
        "https://schema.org/EventPostponed" : 
        "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
    "startDate": auction.start_date,
    "organizer": {
      "@type": "Organization",
      "name": "Florida Highwaymen Auction LLC",
      "url": BASE_URL,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "8214 Griffin Rd",
        "addressLocality": "Davie",
        "addressRegion": "FL",
        "postalCode": "33328",
        "addressCountry": "US"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+1-954-605-0748",
        "contactType": "customer service",
        "email": "info@floridahighwaymenauction.com"
      }
    },
    "location": {
      "@type": "VirtualLocation",
      "url": `${BASE_URL}/auctions/${auction.id}`
    },
    "isAccessibleForFree": true,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${BASE_URL}/auctions/${auction.id}`
    }
  };

  // Add end date if available
  if (auction.end_date) {
    schema.endDate = auction.end_date;
  }

  // Add live streaming information if auction is live
  if (auction.is_live || auction.status === 'live') {
    schema.publication = {
      "@type": "BroadcastEvent",
      "isLiveBroadcast": true,
      "broadcastOfEvent": {
        "@type": "Event",
        "name": auction.title
      }
    };
  }

  // Add audience information
  schema.audience = {
    "@type": "Audience",
    "audienceType": "Art Collectors, Florida Highwaymen Enthusiasts, Art Investors"
  };

  return schema;
};

/**
 * Generate Schema.org Article markup for blog posts
 */
export const generateBlogPostSchema = (post: {
  id: string;
  title: string;
  excerpt?: string;
  content?: string;
  published_at?: string;
  featured_image_url?: string;
  author?: {
    profiles?: {
      first_name?: string;
      last_name?: string;
    };
  };
  category?: {
    name?: string;
  };
}): object => {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${BASE_URL}/blog/${post.id}`,
    "headline": post.title,
    "url": `${BASE_URL}/blog/${post.id}`,
    "description": post.excerpt || `Educational article about Florida Highwaymen art: ${post.title}`,
    "articleSection": post.category?.name || "Florida Highwaymen Art",
    "genre": "Art Education",
    "keywords": "Florida Highwaymen, art education, African American artists, landscape painting",
    "publisher": {
      "@type": "Organization",
      "name": "Florida Highwaymen Auction LLC",
      "url": BASE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/lovable-uploads/a04e8ead-7f23-41b8-91a5-a2427d0afcb6.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${BASE_URL}/blog/${post.id}`
    }
  };

  // Add publication date
  if (post.published_at) {
    schema.datePublished = post.published_at;
    schema.dateModified = post.published_at;
  }

  // Add author information
  if (post.author?.profiles) {
    const { first_name, last_name } = post.author.profiles;
    if (first_name || last_name) {
      schema.author = {
        "@type": "Person",
        "name": `${first_name || ''} ${last_name || ''}`.trim(),
        "jobTitle": "Art Expert",
        "worksFor": {
          "@type": "Organization",
          "name": "Florida Highwaymen Auction LLC"
        }
      };
    }
  }

  // Add featured image
  if (post.featured_image_url) {
    schema.image = {
      "@type": "ImageObject",
      "url": post.featured_image_url.startsWith('http') ? post.featured_image_url : `${BASE_URL}${post.featured_image_url}`,
      "description": `Featured image for ${post.title}`
    };
  }

  return schema;
};

/**
 * Generate Schema.org Organization markup for the auction house
 */
export const generateOrganizationSchema = (): object => {
  return {
    "@context": "https://schema.org",
    "@type": "AuctionHouse",
    "@id": BASE_URL,
    "name": "Florida Highwaymen Auction LLC",
    "alternateName": "Florida Highwaymen Auction",
    "url": BASE_URL,
    "description": "Premier auction house specializing in authentic Florida Highwaymen paintings and fine art by renowned African American landscape artists. Expert authentication, appraisals, and live auctions.",
    "foundingDate": "2020",
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "specialty": [
      "Florida Highwaymen Art",
      "Florida Highwaymen Paintings",
      "African American Landscape Art",
      "Art Authentication",
      "Art Appraisal"
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "8214 Griffin Rd",
      "addressLocality": "Davie",
      "addressRegion": "FL",
      "postalCode": "33328",
      "addressCountry": "US"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-954-605-0748",
      "contactType": "customer service",
      "email": "info@floridahighwaymenauction.com",
      "availableLanguage": "English"
    },
    "logo": {
      "@type": "ImageObject",
      "url": `${BASE_URL}/lovable-uploads/a04e8ead-7f23-41b8-91a5-a2427d0afcb6.png`,
      "width": 512,
      "height": 512
    },
    "sameAs": [
      "https://www.facebook.com/p/FL-Highwaymen-Auction-and-Akashic-Wreckage-Gallery-100092550935882/"
    ],
    "knowsAbout": [
      "Florida Highwaymen",
      "Florida Highwaymen Paintings",
      "African American Art",
      "Landscape Painting",
      "Art Authentication",
      "Art Appraisal",
      "Art Auction",
      "Art Consignment"
    ]
  };
};

/**
 * Generate Schema.org FAQPage markup
 */
export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>): object => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * Generate Schema.org BreadcrumbList markup
 */
export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>): object => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`
    }))
  };
};

/**
 * Generate Schema.org ItemList markup for collections
 */
export const generateItemListSchema = (items: Array<{
  name: string;
  url: string;
  image?: string;
  description?: string;
}>): object => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "url": item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`,
      "name": item.name,
      "image": item.image ? (item.image.startsWith('http') ? item.image : `${BASE_URL}${item.image}`) : undefined,
      "description": item.description
    }))
  };
};

/**
 * Generate Schema.org Service markup for services
 */
export const generateServiceSchema = (service: {
  name: string;
  description: string;
  url: string;
  serviceType: string;
  areaServed?: string;
}): object => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "url": service.url.startsWith('http') ? service.url : `${BASE_URL}${service.url}`,
    "serviceType": service.serviceType,
    "provider": {
      "@type": "Organization",
      "name": "Florida Highwaymen Auction LLC",
      "url": BASE_URL
    },
    "areaServed": service.areaServed || "United States",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    }
  };
};

/**
 * Add schema markup to page head
 */
export const addSchemaToHead = (schema: object): void => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}; 